// SettingsModalButton.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './SettingsModalButton.css';

// Icons
import Icon from 'svg/Icon';

/**
 * SettingsModalButton
 * 
 * Displays a button for the settings modal.
 * 
 * @param {string} iconKey - The key for the button icon.
 * @param {string} label - The button label.
 * @param {boolean} selected - Whether this nav item is the active tab.
 * @param {Function} onClick - Callback when the button is clicked.
 *  
 *
 * @returns {ReactNode} - SettingsModalButton.
 */
const SettingsModalButton = React.memo(({ iconKey, label, color, selected = false, onClick }) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- STATE ----- //

    // Hover State

    const [hovered, setHovered] = useState(false);

    // ----- HANDLERS ----- //

    /**
     * Sets hovered UI state to true.
     */

    const handleMouseEnter = () => {
        setHovered(true);
    };

    /**
     * Sets hovered UI state to false.
     */
    const handleMouseLeave = () => {
        setHovered(false);
    };

    const showSelected = selected;
    const showHover = hovered && !showSelected;

    const backgroundColor = showSelected
        ? theme.backgroundSelected
        : (showHover ? theme.backgroundHover : 'transparent');

    const foregroundColor = showSelected
        ? theme.foregroundSelected
        : theme.foreground;

    const iconColor = color || (showSelected
        ? theme.foregroundSelected
        : (showHover ? theme.foreground : theme.foregroundMuted));

    // ----- RENDER ----- //

    return (

        <div
            className="settings-modal-button-container"
            style={{
                backgroundColor,
                color: foregroundColor,
            }}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >

            {/* ICON */}
            <div className="settings-modal-button-icon">
                <Icon
                    iconKey={iconKey}
                    width={18}
                    height={18}
                    color={iconColor}
                />
            </div>

            {/* LABEL */}
            <div
                className="settings-modal-button-label"
                style={{
                    color: color || theme.foreground,
                }}
            >
                {label}
            </div>

            <div className="settings-modal-button-arrow">
                {showSelected && (
                    <Icon
                        iconKey="arrow_right"
                        width={16}
                        height={16}
                        color={iconColor}
                    />
                )}
            </div>

        </div>
    );

});

// Dev Tools
SettingsModalButton.displayName = 'SettingsModalButton';

export default SettingsModalButton;
